// v2.1 finding #58 (Phase 4 Session 2 Block A2 — cross-surface supersede
// audit, Plan A): cross-surface generalization of the Water-only helper that
// landed in #53. The same dual-label-stacking bug class reproduces on every
// screen that co-renders three independent JSX blocks:
//
//   1. A transient "action message" / status toast (e.g. "registro se guardó
//      localmente. la sincronización está pendiente"). Set by save / retry /
//      refresh handlers, cleared imperatively, scoped per-screen.
//   2. A "last result summary" (sticky outcome of the most recent retry /
//      refresh attempt). Derived from the screen's `*SyncLastOutcome` state.
//   3. An "attention message" (operator-actionable banner saying "you have
//      changes pending sync"). Derived from `*SyncStatus.syncState`.
//
// When push is pending, blocks (1) and (3) can render simultaneously with
// near-duplicate copy ("local save pending" + "changes in queue, pending
// sync"), confusing the operator into reflexive Refresh taps. Block A2
// reproduction (2026-06-30) of the exact same pattern on Biomass:
//
//   "El registro de biomasa se guardó localmente. La sincronización con el
//    servidor está pendiente" (statusMessage)
//     +
//   "los cambios de biomasa están en cola y siguen pendientes de
//    sincronización" (syncAttentionMessage)
//
// This module exports two pure rules so the supersede contract is testable
// without rendering any screen:
//
//   * `computeSyncBannerVisibility` — decides which of two derived slots
//     ("baseline healthy" + "action message") may render given the state of
//     the three independent blocks above. Baseline shows only when all three
//     are empty; action message shows only when attention / last-result are
//     absent (so the operator never sees two near-duplicate labels).
//
//   * `shouldPublishRefreshComplete` — decides whether a post-refresh
//     success toast may be published given the post-refresh sync state. Only
//     when fully `synced`; otherwise the attention banner is authoritative
//     and the toast would contradict it.
//
// The Water-specific module at `src/screens/water/waterSyncBannerVisibility`
// re-exports both functions under their original names for back-compat with
// existing call sites (Water.tsx + the Water test suite).

export type SyncStateLabel = 'synced' | 'pending' | 'offline'

export type SyncBannerVisibilityInput = Readonly<{
  hasActionMessage: boolean
  hasAttentionMessage: boolean
  hasLastResultSummary: boolean
}>

export type SyncBannerVisibility = Readonly<{
  /**
   * Whether the persistent "everything is healthy" baseline block may
   * render. Baseline must be hidden whenever any of the three independent
   * slots is active, so contradictory state messages never coexist.
   *
   * Used by Water (and any future surface that adopts the baseline-block
   * affordance from #17 / #53).
   */
  showBaseline: boolean
  /**
   * Whether the transient action-message slot (post-save toast, refresh
   * status, retry status) may render. Action message must be hidden
   * whenever an attention banner or last-result summary is showing,
   * because both convey the same authoritative state in richer form
   * and the action message would duplicate them (v2.1 finding #58).
   *
   * Used by Biomass / Feed / Tasks / EditFarm / Lots (the five surfaces
   * confirmed during the 2026-06-30 cross-surface audit to co-render
   * status + attention + lastResult JSX blocks independently).
   */
  showActionMessage: boolean
}>

export const computeSyncBannerVisibility = (
  input: SyncBannerVisibilityInput
): SyncBannerVisibility => {
  const otherSlotActive = input.hasAttentionMessage || input.hasLastResultSummary
  return {
    showBaseline: !input.hasActionMessage && !otherSlotActive,
    showActionMessage: input.hasActionMessage && !otherSlotActive
  }
}

/**
 * Should the pull-success ("refreshComplete") toast be published after a
 * successful refresh round-trip? Only when the post-refresh sync state is
 * fully `synced`. Pending / offline / blocked states leave the attention
 * banner as the authoritative signal and suppress the potentially-misleading
 * "actualizado desde el servidor" toast.
 *
 * `null` / `undefined` is treated as not-synced: if the caller could not
 * derive a post-refresh sync status, err on the side of NOT publishing a
 * contradictory success message.
 */
export const shouldPublishRefreshComplete = (
  postRefreshSyncState: SyncStateLabel | null | undefined
): boolean => postRefreshSyncState === 'synced'
